import axios from 'axios';
import localDB from './localDB';
import networkService from './networkService';
import { isCacheableReadError, isOfflineQueueableError } from '../utils/offlineError';
import { decodeRequestData } from '../utils/requestData';
import { taskScheduleParts } from '../utils/taskSchedule';
import {
  buildFinancePeriods,
  buildFinanceStats,
  currentMonthKey,
  financePeriodKey,
  visibleFinances,
} from '../utils/financeReports';

// Определяем API URL в зависимости от окружения
// Для веб-версии (через nginx): относительный путь /api
// Для мобильного приложения: полный URL к production API
const getApiUrl = () => {
  // Если указана переменная окружения, используем её
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Проверяем, запущено ли приложение в Capacitor (мобильное приложение)
  // Проверяем по протоколу - capacitor:// или file:// означает нативное приложение
  const isNative = window.location.protocol === 'capacitor:' ||
                   window.location.protocol === 'file:' ||
                   (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

  if (isNative) {
    // В мобильном приложении используем production API
    return process.env.REACT_APP_NATIVE_API_URL || 'https://crm.savel-ev.ru/api';
  }

  // В веб-версии используем относительный путь (nginx проксирует на backend:3000)
  return '/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 секунд таймаут для мобильных соединений
});

// Add token to requests
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors and offline mode
api.interceptors.response.use(
  (response) => {
    // Сохраняем успешные ответы в локальную БД
    if (response.config.url && response.data) {
      saveToLocalDB(response.config.method, response.config.url, response.data).catch(err => {
        // Проверяем ConstraintError и не логируем его
        const errorName = String(err?.name || '');
        const innerName = String(err?.inner?.name || err?._e?.name || '');
        const errorMessage = String(err?.message || '');
        const innerMessage = String(err?.inner?.message || err?._e?.message || '');

        const isConstraint = errorName === 'ConstraintError' ||
                            innerName === 'ConstraintError' ||
                            errorMessage.includes('ConstraintError') ||
                            errorMessage.includes('already exists') ||
                            errorMessage.includes('Key already exists') ||
                            innerMessage.includes('ConstraintError') ||
                            innerMessage.includes('already exists') ||
                            innerMessage.includes('Key already exists');

        // НЕ логируем ConstraintError
        if (!isConstraint) {
          console.error('Error saving to local DB:', err);
        }
      });
    }
    return response;
  },
  async (error) => {
    const isCacheableError = isCacheableReadError(error);
    const isQueueableError = isOfflineQueueableError(error);

    // Для GET запросов при ошибках пытаемся вернуть данные из локальной БД / localStorage
    if (error.config && error.config.method === 'get') {
      // Если это ошибка сервера (5xx) или нет ответа от сервера
      if (isCacheableError) {
        try {
          const localData = await getFromLocalDB(
            error.config.method,
            error.config.url,
            error.config.params
          );
          if (localData != null) {
            // Не логируем как ошибку — данные есть, приложение работает
            return Promise.resolve({
              data: localData,
              status: 200,
              statusText: 'OK',
              headers: {},
              config: error.config,
              fromCache: true,
              serverError: Boolean(error.response)
            });
          }
        } catch (dbError) {
          console.error('Error getting data from local DB:', dbError);
        }
      }
    }

    // При 502/503/504 на POST /tasks сохраняем в очередь и локально (только если это НЕ повтор из очереди синка)
    const fromSyncQueue = error.config?._fromSyncQueue === true;
    if (!fromSyncQueue && !error.config?._offlineHandled && isQueueableError && error.config && error.config.method?.toLowerCase() === 'post') {
      const url = String(error.config.url || '');
      const data = decodeRequestData(error.config.data);
      const completeMatch = url.match(/\/tasks\/(\d+)\/complete\/?$/);
      if (completeMatch) {
        try {
          await saveToSyncQueue(error.config.method, error.config.url, null, error.config.headers || {});
          await localDB.updateTask(completeMatch[1], { status: 'completed', completed_at: new Date().toISOString() }, true);
          error.queuedForSync = true;
        } catch (e) {
          console.error('Failed to queue complete for sync:', e);
        }
      } else if (url.includes('/tasks') && !url.match(/\/tasks\/\d+\//)) {
        try {
          await saveToSyncQueue(error.config.method, error.config.url, data, error.config.headers || {});
          if (url.endsWith('/tasks') || url.includes('/tasks?')) {
            await localDB.addTask(data, true);
          }
          error.queuedForSync = true;
        } catch (e) {
          console.error('Failed to queue task for sync:', e);
        }
      }
    }

    // Логируем ошибки для отладки (не спамим при 502, если запрос уже в очереди)
    if (error.response) {
      if (!error.queuedForSync) {
        console.error('API Error:', error.response.status, error.response.data);
      }
    } else if (error.request) {
      console.error('API Request Error:', error.request);

      // Если запрос не был отправлен (нет интернета), сохраняем в очередь (не если это уже запрос из очереди)
      const isOnline = await networkService.isConnected();
      if ((!isOnline || isQueueableError) && !error.config?._fromSyncQueue && !error.config?._offlineHandled) {
        console.log('No internet connection');
        if (error.config && ['post', 'put', 'delete'].includes(error.config.method?.toLowerCase())) {
          await saveToSyncQueue(
            error.config.method,
            error.config.url,
            decodeRequestData(error.config.data),
            error.config.headers
          );
        }
      }
    } else {
      console.error('API Error:', error.message);
    }

    // Обрабатываем ошибку авторизации
    if (error.response?.status === 401) {
      // Не делаем редирект для /auth/me и /auth/login, так как это проверка аутентификации
      const isAuthCheck = error.config?.url?.includes('/auth/me') || error.config?.url?.includes('/auth/login');
      if (!isAuthCheck) {
        // Для других запросов очищаем токен
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // В мобильном приложении используем навигацию через React Router
        // В веб-версии используем window.location
        const isNative = window.location.protocol === 'capacitor:' ||
                         window.location.protocol === 'file:' ||
                         (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
        if (!isNative && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          window.location.href = '/login';
        }
      }
    }

    // Для всех остальных ошибок возвращаем ошибку
    return Promise.reject(error);
  }
);

/**
 * Сохранить данные в локальную БД
 */
async function saveToLocalDB(method, url, data) {
  try {
    if (method === 'get' && data) {
      // Определяем тип данных по URL
      if (url.includes('/tasks')) {
        if (Array.isArray(data)) {
          for (const task of data) {
            // addTask теперь сам обрабатывает все ошибки, включая ConstraintError
            // Обертываем в try-catch, чтобы полностью подавить ConstraintError
            try {
              await localDB.addTask(task, false);
            } catch (taskError) {
              // Проверяем ConstraintError и игнорируем его
              const errorName = String(taskError?.name || '');
              const innerName = String(taskError?.inner?.name || taskError?._e?.name || '');
              const errorMessage = String(taskError?.message || '');
              const innerMessage = String(taskError?.inner?.message || taskError?._e?.message || '');

              const isConstraint = errorName === 'ConstraintError' ||
                                  innerName === 'ConstraintError' ||
                                  errorMessage.includes('ConstraintError') ||
                                  errorMessage.includes('already exists') ||
                                  errorMessage.includes('Key already exists') ||
                                  innerMessage.includes('ConstraintError') ||
                                  innerMessage.includes('already exists') ||
                                  innerMessage.includes('Key already exists');

              // НЕ логируем ConstraintError
              if (!isConstraint) {
                console.error('Error adding task to local DB:', taskError);
              }
            }
          }
        } else {
          try {
            await localDB.addTask(data, false);
          } catch (taskError) {
            const errorName = String(taskError?.name || '');
            const innerName = String(taskError?.inner?.name || taskError?._e?.name || '');
            const errorMessage = String(taskError?.message || '');
            const innerMessage = String(taskError?.inner?.message || taskError?._e?.message || '');

            const isConstraint = errorName === 'ConstraintError' ||
                                innerName === 'ConstraintError' ||
                                errorMessage.includes('ConstraintError') ||
                                errorMessage.includes('already exists') ||
                                errorMessage.includes('Key already exists') ||
                                innerMessage.includes('ConstraintError') ||
                                innerMessage.includes('already exists') ||
                                innerMessage.includes('Key already exists');

            if (!isConstraint) {
              console.error('Error adding task to local DB:', taskError);
            }
          }
        }
      } else if (url.includes('/clients')) {
        if (Array.isArray(data)) {
          for (const client of data) {
            // addClient теперь сам обрабатывает все ошибки
            await localDB.addClient(client, false);
          }
        } else {
          await localDB.addClient(data, false);
        }
      } else if (/^\/finances(?:\?|$)/.test(url)) {
        if (Array.isArray(data)) {
          for (const finance of data) {
            // addFinance теперь сам обрабатывает все ошибки
            await localDB.addFinance(finance, false);
          }
        } else {
          await localDB.addFinance(data, false);
        }
      }
    }
  } catch (error) {
    // Функция для проверки ConstraintError
    const isConstraintError = (err) => {
      if (!err) return false;
      const errorMessage = String(err.message || '');
      const errorName = String(err.name || '');
      const innerError = err.inner || err._e;
      const innerName = innerError ? String(innerError.name || '') : '';
      const innerMessage = innerError ? String(innerError.message || '') : '';

      // Проверяем все возможные варианты
      if (errorName === 'ConstraintError' || innerName === 'ConstraintError') return true;
      if (errorMessage.includes('ConstraintError')) return true;
      if (errorMessage.includes('already exists')) return true;
      if (errorMessage.includes('Key already exists')) return true;
      if (innerMessage.includes('ConstraintError')) return true;
      if (innerMessage.includes('already exists')) return true;
      if (innerMessage.includes('Key already exists')) return true;

      return false;
    };

    // НЕ логируем ConstraintError - это нормальная ситуация при синхронизации
    if (!isConstraintError(error)) {
      console.error('Error saving to local DB:', error);
    }
    // Полностью игнорируем ConstraintError - не пробрасываем и не логируем
  }
}

/**
 * Получить данные из локальной БД
 */
async function getFromLocalDB(method, url, requestParams = {}) {
  try {
    if (method === 'get') {
      // При 502 — отдаём данные из localStorage, чтобы дашборд не падал
      if (url.includes('/auth/me')) {
        try {
          const saved = localStorage.getItem('user');
          if (saved) {
            const user = JSON.parse(saved);
            return { user };
          }
          return { user: null };
        } catch (_) {
          return { user: null };
        }
      }
      if (url.includes('/users/settings')) {
        const defaults = {
          show_expenses: true,
          show_address: true,
          show_client: true,
          show_cost: true
        };
        try {
          const saved = localStorage.getItem('settings');
          if (saved) {
            const parsed = JSON.parse(saved);
            return { ...defaults, ...parsed };
          }
        } catch (_) {}
        return defaults;
      }
      // Определяем тип данных по URL
      if (url.includes('/analytics/dashboard')) {
        // Для dashboard формируем данные из локальной БД
        try {
          const tasks = await localDB.getTasks();
          const clients = await localDB.getClients();
          const finances = await localDB.getFinances();
          const workTasks = (tasks || []).filter((task) => task?.is_personal !== true);
          const workFinances = visibleFinances(finances, tasks);

          // Формируем данные для dashboard
          const monthKey = currentMonthKey();
          const monthFinances = workFinances.filter((finance) => (
            finance.type === 'income'
            && financePeriodKey(finance.date || finance.created_at, 'month') === monthKey
          ));
          const monthIncome = monthFinances.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);

          // Подсчитываем общий доход
          const totalIncome = workFinances
            .filter(f => f.type === 'income')
            .reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);

          // Группируем задачи по статусу
          const tasksByStatus = {};
          workTasks.forEach(task => {
            const status = task.status || 'pending';
            tasksByStatus[status] = (tasksByStatus[status] || 0) + 1;
          });

          // Формируем массив задач по статусу
          const tasksByStatusArray = Object.entries(tasksByStatus).map(([status, count]) => ({
            status,
            count
          }));

          // Топ клиентов по рейтингу
          const topClients = clients
            .sort((a, b) => (b.rating || 0) - (a.rating || 0))
            .slice(0, 5)
            .map(client => ({
              id: client.id,
              name: client.name,
              rating: client.rating || 0
            }));

          // Недавно завершенные — сортировка и дата по дате создания задачи
          const incomeTaskIds = new Set(
            workFinances
              .filter((finance) => finance.type === 'income' && finance.task_id != null)
              .map((finance) => String(finance.task_id))
          );
          const recentTasks = workTasks
            .filter(task => task.status === 'completed')
            .sort((a, b) => {
              const dateA = new Date(a.created_at || 0);
              const dateB = new Date(b.created_at || 0);
              return dateB - dateA;
            })
            .slice(0, 5)
            .map(task => ({
              id: task.id,
              title: task.title,
              report_display_date: task.created_at ? (typeof task.created_at === 'string' ? task.created_at.slice(0, 10) : new Date(task.created_at).toISOString().slice(0, 10)) : null,
              created_at: task.created_at,
              cost: task.cost,
              payment_status: incomeTaskIds.has(String(task.server_id ?? task.id)) ? 'paid' : 'unpaid',
            }));

          return {
            monthIncome,
            totalIncome,
            tasks: {
              byStatus: tasksByStatusArray,
              total: tasks.length
            },
            clients: {
              total: clients.length,
              topRated: topClients
            },
            recentCompleted: recentTasks,
            fromCache: true
          };
        } catch (err) {
          console.error('Error generating dashboard from local DB:', err);
          return null;
        }
      } else if (url.includes('/calendar')) {
        const tasks = await localDB.getTasks();
        return tasks.map((task) => {
          const hasDueDate = Boolean(taskScheduleParts(task.due_date));
          const dueDate = hasDueDate ? task.due_date : null;
          return {
            ...task,
            id: task.server_id || task.temp_id || task.id,
            start: dueDate,
            end: dueDate,
            due_date: dueDate,
            client: task.client_name || null,
            project: task.project_name || null,
          };
        });
      } else if (url.includes('/tasks')) {
        return await localDB.getTasks();
      } else if (url.includes('/clients')) {
        return await localDB.getClients();
      } else if (url.includes('/finances/reports-stats')) {
        try {
          const tasks = await localDB.getTasks().catch(() => []);
          const finances = await localDB.getFinances().catch(() => []);
          return buildFinanceStats(finances, tasks);
        } catch (err) {
          console.error('Error building reports-stats from local DB:', err);
          return null;
        }
      } else if (url.includes('/finances/by-month')) {
        try {
          const groupMatch = url.match(/[?&]group=(\w+)/);
          const group = String(requestParams?.group || (groupMatch ? groupMatch[1] : 'month')).toLowerCase();
          const validGroup = ['day', 'week', 'month'].includes(group) ? group : 'month';
          const tasks = await localDB.getTasks().catch(() => []);
          const finances = await localDB.getFinances().catch(() => []);
          return buildFinancePeriods(finances, tasks, validGroup);
        } catch (err) {
          console.error('Error building by-month from local DB:', err);
          return null;
        }
      } else if (url.includes('/analytics/chart/finances')) {
        try {
          const group = String(requestParams?.period || 'month').toLowerCase();
          const [tasks, finances] = await Promise.all([
            localDB.getTasks().catch(() => []),
            localDB.getFinances().catch(() => []),
          ]);
          return buildFinancePeriods(finances, tasks, group).flatMap((row) => {
            const values = [];
            if (row.income > 0) {
              values.push({ period: row.period, type: 'income', total: String(row.income) });
            }
            if (row.expense > 0) {
              values.push({ period: row.period, type: 'expense', total: String(row.expense) });
            }
            return values;
          });
        } catch (err) {
          console.error('Error generating finance chart from local DB:', err);
          return null;
        }
      } else if (url.includes('/analytics/chart/tasks')) {
        // Для графика задач формируем данные из локальной БД
        try {
          await localDB.getTasks();
          return {
            labels: [],
            completed: [],
            pending: [],
            fromCache: true
          };
        } catch (err) {
          console.error('Error generating tasks chart from local DB:', err);
          return null;
        }
      } else if (/^\/finances(?:\?|$)/.test(url)) {
        const [tasks, finances] = await Promise.all([
          localDB.getTasks().catch(() => []),
          localDB.getFinances().catch(() => []),
        ]);
        const startDate = requestParams?.start_date || null;
        const endDate = requestParams?.end_date || null;
        return visibleFinances(finances, tasks).filter((finance) => {
          const date = financePeriodKey(finance.date || finance.created_at, 'day');
          if (!date) return false;
          if (startDate && date < startDate) return false;
          if (endDate && date > endDate) return false;
          return true;
        });
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting from local DB:', error);
    return null;
  }
}

/**
 * Сохранить запрос в очередь синхронизации
 */
async function saveToSyncQueue(method, url, data, headers) {
  try {
    await localDB.addToSyncQueue(
      method.toUpperCase(),
      url,
      decodeRequestData(data),
      headers,
      0
    );
    console.log('Request saved to sync queue:', method, url);
  } catch (error) {
    console.error('Error saving to sync queue:', error);
  }
}

/**
 * Обертка для API запросов с поддержкой оффлайн режима
 */
const apiWithOffline = {
  async get(url, config = {}) {
    try {
      const isOnline = await networkService.isConnected();

      if (isOnline) {
        // Если онлайн, делаем запрос на сервер
        return await api.get(url, config);
      } else {
        // Если оффлайн, получаем данные из локальной БД
        console.log('Offline mode: getting data from local DB');
        const localData = await getFromLocalDB('get', url);
        if (localData) {
          return { data: localData, fromCache: true };
        }
        throw new Error('No data available offline');
      }
    } catch (error) {
      // Если ошибка и есть данные в локальной БД, возвращаем их
      const localData = await getFromLocalDB('get', url);
      if (localData) {
        return { data: localData, fromCache: true };
      }
      throw error;
    }
  },

  async post(url, data, config = {}) {
    try {
      const isOnline = await networkService.isConnected();

      if (isOnline) {
        // Если онлайн, отправляем на сервер
        return await api.post(url, data, config);
      } else {
        // Если оффлайн, сохраняем в локальную БД и очередь синхронизации
        console.log('Offline mode: saving to local DB and sync queue');
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await saveToSyncQueue('post', url, data, headers);

        // Сохраняем в локальную БД с пометкой, что это локальная запись
        if (url.includes('/tasks')) {
          await localDB.addTask(data, true);
        } else if (url.includes('/clients')) {
          await localDB.addClient(data, true);
        } else if (url.includes('/finances')) {
          await localDB.addFinance(data, true);
        }

        return { data: { ...data, id: `temp_${Date.now()}` }, fromCache: true, offline: true };
      }
    } catch (error) {
      // Если ошибка, но мы оффлайн, сохраняем в очередь
      const isOnline = await networkService.isConnected();
      if (!isOnline) {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await saveToSyncQueue('post', url, data, headers);

        if (url.includes('/tasks')) {
          await localDB.addTask(data, true);
        } else if (url.includes('/clients')) {
          await localDB.addClient(data, true);
        } else if (url.includes('/finances')) {
          await localDB.addFinance(data, true);
        }

        return { data: { ...data, id: `temp_${Date.now()}` }, fromCache: true, offline: true };
      }
      throw error;
    }
  },

  async put(url, data, config = {}) {
    try {
      const isOnline = await networkService.isConnected();

      if (isOnline) {
        // Если онлайн, отправляем на сервер
        return await api.put(url, data, config);
      } else {
        // Если оффлайн, сохраняем в локальную БД и очередь синхронизации
        console.log('Offline mode: updating local DB and sync queue');
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await saveToSyncQueue('put', url, data, headers);

        // Обновляем в локальной БД
        const taskId = url.match(/\/(\d+)$/)?.[1];
        if (url.includes('/tasks') && taskId) {
          await localDB.updateTask(taskId, data, true);
        } else if (url.includes('/clients') && taskId) {
          await localDB.clients.where('server_id').equals(parseInt(taskId)).modify(data);
        } else if (url.includes('/finances') && taskId) {
          await localDB.finances.where('server_id').equals(parseInt(taskId)).modify(data);
        }

        return { data: { ...data, id: taskId }, fromCache: true, offline: true };
      }
    } catch (error) {
      // Если ошибка, но мы оффлайн, сохраняем в очередь
      const isOnline = await networkService.isConnected();
      if (!isOnline) {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await saveToSyncQueue('put', url, data, headers);

        const taskId = url.match(/\/(\d+)$/)?.[1];
        if (url.includes('/tasks') && taskId) {
          await localDB.updateTask(taskId, data, true);
        }

        return { data: { ...data, id: taskId }, fromCache: true, offline: true };
      }
      throw error;
    }
  },

  async delete(url, config = {}) {
    try {
      const isOnline = await networkService.isConnected();

      if (isOnline) {
        // Если онлайн, отправляем на сервер
        return await api.delete(url, config);
      } else {
        // Если оффлайн, сохраняем в очередь синхронизации
        console.log('Offline mode: adding delete to sync queue');
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await saveToSyncQueue('delete', url, null, headers);

        // Удаляем из локальной БД
        const taskId = url.match(/\/(\d+)$/)?.[1];
        if (url.includes('/tasks') && taskId) {
          await localDB.deleteTask(taskId);
        }

        return { data: { id: taskId, deleted: true }, fromCache: true, offline: true };
      }
    } catch (error) {
      // Если ошибка, но мы оффлайн, сохраняем в очередь
      const isOnline = await networkService.isConnected();
      if (!isOnline) {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await saveToSyncQueue('delete', url, null, headers);

        const taskId = url.match(/\/(\d+)$/)?.[1];
        if (url.includes('/tasks') && taskId) {
          await localDB.deleteTask(taskId);
        }

        return { data: { id: taskId, deleted: true }, fromCache: true, offline: true };
      }
      throw error;
    }
  }
};

// Экспортируем оба варианта API
export default api;
export { apiWithOffline };
